/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import type { AmazonPlaybackQuality, TrackNerdStats } from '@amzn/vinyl-amzn'
import { data } from '@amazon/vinyl-observable'
import { EventHostImpl } from '@amazon/vinyl-util'
import {
    NULL_AD_SOURCE,
    NULL_CAPTION_SOURCE,
    NULL_SECTION_NAV,
    type PlaybackError,
    type Player,
    type PlayerEventMap,
    type AmznPlaybackQualitySource,
} from '@/playback/api'

/**
 * A do-nothing {@link Player} used when the app boots inside a static render
 * (see the `staticPage` flag in `@/staticRender`). A headless render has no
 * interactive user and must never touch browser-only media machinery: returning
 * this stub keeps `@amzn/vinyl-amzn` unimported and the singleton `<video>`
 * element (MSE/EME) uncreated, while still satisfying the {@link Player} surface
 * the boot-time bindings type against. Every cell holds an idle default and
 * never changes; commands are benign no-ops (async ones resolve).
 */
export function createStaticRenderPlayer(): Player & AmznPlaybackQualitySource {
    return new StaticRenderPlayer()
}

class StaticRenderPlayer
    extends EventHostImpl<PlayerEventMap>
    implements Player, AmznPlaybackQualitySource
{
    readonly playing$ = data(false)
    readonly paused$ = data(true)
    readonly playRequested$ = data(false)
    readonly ended$ = data(false)
    readonly loading$ = data(false)
    readonly currentTime$ = data(0)
    readonly duration$ = data(NaN)
    readonly contentTime$ = data(0)
    readonly contentDuration$ = data(NaN)
    readonly currentTimePercent$ = data(0)
    readonly fetchedTimePercent$ = data(0)
    readonly volume$ = data(1)
    readonly muted$ = data(false)
    readonly playbackRate$ = data(1)
    readonly loop$ = data(false)
    readonly error$ = data<PlaybackError | null>(null)
    readonly playbackQuality$ = data<AmazonPlaybackQuality | null>(null)
    readonly trackNerdStats$ = data<TrackNerdStats | null>(null)
    readonly amznPlaybackQuality: AmznPlaybackQualitySource = this
    readonly captions = NULL_CAPTION_SOURCE
    readonly sections = NULL_SECTION_NAV
    readonly ads = NULL_AD_SOURCE

    play(): Promise<void> {
        return Promise.resolve()
    }
    pause(): void {}
    seekTo(): Promise<void> {
        return Promise.resolve()
    }
    preload(): void {}
    getQualitySelectionReason(): null {
        return null
    }
    setStreamingQuality(): void {}
}
